import { surveyDefinitions } from '@shared';
import { INTAKE_FORM_INCOMPLETE, STARTED_INTAKE_FORM } from '@/util/constants';

/**
 * The client's intake survey, even when the client-side calculations have not run.
 *
 * `intakeSurvey` is attached by `calcClientDerivatives`, which the client reducer skips for a
 * client with no prediction yet (for example one whose Quick Plan model run failed). The survey is
 * still in `surveys`, as the server sent it.
 */
export function findClientIntakeSurvey(
  client: Pick<Client, 'intakeSurvey' | 'surveys'>,
): IntakeSurvey | undefined {
  return (
    client.intakeSurvey ??
    (client.surveys?.find(
      survey => survey.surveyDefinitionId === surveyDefinitions.intakeForm.surveyDefinitionId,
    ) as IntakeSurvey | undefined)
  );
}

export function clientNeedsFullIntakeForm(
  client: Client | null,
  genworthMode = false,
): boolean {
  if (!client) return false;
  // Genworth has no medical questionnaire or full intake flow, so a basic-info client is
  // considered complete — the questionnaire prompt and alert never apply.
  if (genworthMode) return false;
  if (client.formReferenceStatus === 'completed') return false;
  // A form that was completed and later "reopened" (e.g. advisor clicked Update Intake Form,
  // client left it midway) has its live status reset to 'sent' while the completion lives on in
  // FormProviderReferenceHistory. Prefer that historical completion over the stale live status.
  if (client.formReferenceEverCompleted === true) return false;
  if (client.metadata?.assumptionsSet === true) return false;
  if (client.formReferenceId) return true;

  const hasIncompleteTag = client.clientTags?.some(
    tag => tag === INTAKE_FORM_INCOMPLETE || tag === STARTED_INTAKE_FORM,
  );

  const isQuickPlanClient = client.metadata?.createdVia === 'short-intake-form';

  return hasIncompleteTag || isQuickPlanClient;
}

export function toNoonUTC(date: Date): Date {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0));
}

/** `YYYY-MM-DD` in local time, so the day the user picked is the day that is sent. */
export function toDateOnlyString(date: Date | undefined): string {
  if (!date) return '';
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/** Inverse of `toDateOnlyString`, in local time so the calendar shows the day that was sent. */
export function fromDateOnlyString(value: string): Date | undefined {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!parts) return undefined;
  return new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
}
